/* eslint-disable import/no-unresolved */
import * as request from 'superagent'
import { getStore } from 'lib/Request'
import { getMerchantUuid } from 'sideEffects/common'
import * as uuidv4 from 'uuid/v4'
import * as Cookies from 'js-cookie'
import { NODE_API_URL, API_VERSION, ENV } from 'config'
import { parse } from 'query-string'
import browserHistory from 'common/browserHistory'
import { getLogger, logLevels } from '@platform/logger'
import { getCookieGDPRFlags } from 'lib/CookieGDPR/getCookieGDPRFlags'
import { addMinutes } from 'date-fns'
import { getCid, updateCid } from '@auto1-commons/auto1-cid'

const USER_HASH_KEY = 'user_hash'
const SESSION_EXPIRES_IN_MINUTES = 30
const SESSION_ID_KEY = 'auto1_tracking_session'

interface IWindow extends Window {
  ga: any
  dataLayer: any
}

// eslint-disable-next-line no-var
declare var window: IWindow

const getTrackingSessionIdFromCookie = (): string => {
  const trackingSessionId = Cookies.get(SESSION_ID_KEY) || uuidv4()

  Cookies.set(SESSION_ID_KEY, trackingSessionId, {
    expires: addMinutes(new Date(), SESSION_EXPIRES_IN_MINUTES)
  })

  return trackingSessionId
}

const getTrackingSessionId = (): string => {
  if (window.tracking) {
    return window.tracking?.sessionId
  }

  return getTrackingSessionIdFromCookie()
}

const getAISearchId = (): string | null => {
  if (window.tracking) {
    return window.tracking?.ai.getAISearchId()
  }

  return null
}

export const formatCustomData = data => ({
  ...data,
  hitType: data.hitType === undefined ? 'event' : data.hitType
})

export const formatCustomDataForInternalTracking = data => {
  const {
    eventAction: action,
    eventLabel: label,
    eventValue: value,
    eventCategory: category,
    source_url = window.location.href,
    type,
    appname,
    ...rest
  } = data

  let userHash = Cookies.get(USER_HASH_KEY)

  if (!userHash) {
    userHash = uuidv4()
    Cookies.set(USER_HASH_KEY, userHash)
  }

  const query = parse(browserHistory.location.search)
  let userId = ''
  try {
    userId = getStore ? getMerchantUuid(getStore()) : rest.uuid
  } catch {
    // ignore
  }

  if (!userId) {
    const logger = getLogger()
    logger.log(
      {
        msg: `Invalid userId / typeof getStore ${typeof getStore}`,
        error: 'missing_userid', // label to find those logs in kibana later
        errorWindowHandler: false
      },
      logLevels.ERROR
    )
  }

  return {
    ...rest,
    action,
    label,
    value,
    category: category || 'UserEvent',
    type: type || 'auto1',
    session_id: getTrackingSessionId(),
    ai_search_id: getAISearchId(),
    cid: getCid(),
    utm_source:
      query.utm_source || (localStorage && localStorage.getItem('utm_source')),
    utm_medium:
      query.utm_medium || (localStorage && localStorage.getItem('utm_medium')),
    referer: document.referrer || undefined,
    appname: appname || 'auto1v2',
    timestamp: data.timestamp || Math.round(Date.now() / 1000),
    appver: '1.0.0',
    source_url,
    user_token: userHash,
    user_id: userId,
    user_agent: window.navigator.userAgent
  }
}

const exposeInternalTracking = (trackingEvent: {
  [key: string]: string | number
}) => {
  if (
    !Array.isArray(window['QAInternalTracking']) ||
    window['QAInternalTracking'].length > 25
  ) {
    window['QAInternalTracking'] = []
  }

  window['QAInternalTracking'].push(trackingEvent)
}

export const GoogleAnalyticsHelper = {
  sendUserEvent(action: string, label: string, value = null): void {
    if (!window.dataLayer) {
      window.dataLayer = []
    }

    window.dataLayer.push({
      hitType: 'event',
      eventCategory: 'UserEvent',
      event: action,
      eventLabel: label,
      eventValue: value
    })
  },
  custom(data): void {
    if (!window.dataLayer) {
      window.dataLayer = []
    }
    const eventData = formatCustomData(data)
    window.dataLayer.push({ ...eventData, event: eventData.eventAction })
  },
  prevPage: document.referrer,
  updatePage(url?: string): void {
    if (!url) {
      url = window.location.href
    }

    updateCid('', this.prevPage)

    this.prevPage = url

    if (!window.dataLayer) {
      window.dataLayer = []
    }
    window.dataLayer.push({ event: 'virtualPageView', url })
  }
}

export const InternalTrackingHelper = {
  sendUserEvent(
    eventAction: string,
    eventLabel: string,
    eventValue = null
  ): void {
    const event = {
      eventAction,
      eventCategory: 'UserEvent',
      eventLabel,
      eventValue
    }
    this.custom(event)
    exposeInternalTracking(event)
  },
  async custom(data): Promise<void> {
    const { isAnalyticalCookieEnabled } = getCookieGDPRFlags()

    if (!isAnalyticalCookieEnabled) {
      return
    }

    const formatted = formatCustomDataForInternalTracking(data)
    if (ENV === 'DEV') {
      console.info(`📊tracking:${formatted.action}`, formatted)
    } else {
      request
        .post(
          `${
            (window as any).nodejsLayerUrl || NODE_API_URL
          }/${API_VERSION}/tracking`
        )
        .type('json')
        .send(formatted)
        .end()

      exposeInternalTracking(formatted)
    }
  }
}

export const GeneralTrackingHelper = {
  sendUserEvent(action: string, label: string, value = null): void {
    GoogleAnalyticsHelper.sendUserEvent(action, label, value)
    InternalTrackingHelper.sendUserEvent(action, label, value)
  },
  custom(data): void {
    GoogleAnalyticsHelper.custom(data)
    InternalTrackingHelper.custom(data)
  },
  sendGoogleTag(data: {
    event: string
    uuid?: string
    value?: string | number
  }): void {
    try {
      window.dataLayer &&
        window.dataLayer.push({
          event: data.event,
          marketingCampaign: parse(browserHistory.location.search).utm_source,
          uuid: getStore ? getMerchantUuid(getStore()) : data.uuid,
          value: data.value
        })
    } catch {
      // ignore
    }
  }
}

// expose tracking event lib for MFE apps
if (!window.GeneralTrackingHelper) {
  window.GeneralTrackingHelper = GeneralTrackingHelper
}

export default {
  ga: GoogleAnalyticsHelper,
  internal: InternalTrackingHelper,
  general: GeneralTrackingHelper
}
